/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Components
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {configureRoutes} from './utils/routes-utils'
import {routes as _routes} from '@salesforce/retail-react-app/app/routes'

const fallback = <Skeleton height="75vh" width="100%" />

// Create your pages here and add them to the routes array
// Use loadable to split code into smaller js chunks
const OrphanBasketCustomerReturn = loadable(
    () => import('./pages/splio/orphan-basket-customer-return'),
    {fallback}
)
const Home = loadable(() => import('./pages/home'), {fallback})
const Login = loadable(() => import('./pages/login'), {fallback})
const Registration = loadable(() => import('./pages/registration'), {
    fallback
})
const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})
const SetNewPassword = loadable(() => import('./pages/set-new-password'), {fallback})
const Account = loadable(() => import('./pages/account'), {fallback})
const Styleguide = loadable(() => import('./pages/styleguide'))
const PageViewer = loadable(() => import('./pages/page-viewer'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const Newsletter = loadable(() => import('./pages/newsletter'), {fallback})
const Invoice = loadable(() => import('./pages/invoice'), {fallback})

// Checkout page from Adyen
const Checkout = loadable(() => import('./pages/checkout'), {
    fallback: fallback
})

// CheckoutConfirmation page from Adyen
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {
    fallback: fallback
})

// Checkout Redirect page from Adyen
const AdyenCheckoutRedirect = loadable(() => import('./pages/checkout/redirect'), {
    fallback: fallback
})

// Checkout Error page from Adyen
const AdyenCheckoutError = loadable(() => import('./pages/checkout/error'), {
    fallback: fallback
})

const ProductSet = loadable(() => import('./pages/product-set'), {fallback})
const Stores = loadable(() => import('./pages/stores'), {fallback})

const LegacyURLs = loadable(() => import('./pages/legacy-urls'))
const GiftCard = loadable(() => import('./pages/gift-card'), {fallback})

// Sitemap
const Sitemap = loadable(() => import('./pages/sitemap'), {fallback})

// 404 page
const PageNotFound = loadable(() => import('./pages/page-not-found'))

import {PreparationPage} from './pages/preparation-page'

// Non outlet routes
const routes = [
    {
        path: '/home',
        component: Home,
        exact: true
    },
    {
        path: '/login',
        component: Login,
        exact: true
    },
    {
        path: '/registration',
        component: Registration,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/set-new-password',
        component: SetNewPassword,
        exact: true
    },
    {
        path: '/account',
        component: Account
    },
    {
        path: '/styleguide',
        component: Styleguide
    },
    {
        path: '/page/:pageId',
        component: PageViewer
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/checkout/redirect',
        component: AdyenCheckoutRedirect
    },
    {
        path: '/checkout/error',
        component: AdyenCheckoutError
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: CheckoutConfirmation
    },
    {
        path: '/api/splio/orphan-basket-customer-return',
        component: OrphanBasketCustomerReturn
    },
    {
        path: '/cart',
        component: Cart,
        exact: true
    },
    {
        path: '/newsletter',
        component: Newsletter,
        exact: true
    },
    {
        path: '/invoice/:orderNo',
        component: Invoice,
        exact: true
    },
    {
        path: '/stores',
        component: Stores
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/mosaic/:categoryId',
        component: ProductList
    },
    {
        path: '/category/:categoryId',
        component: ProductList
    },
    {
        path: '/c/*/:categoryId',
        component: ProductList
    },
    {
        path: '/c/:categoryId',
        component: ProductList
    },
    {
        path: '/product/:productId',
        component: ProductDetail
    },
    {
        path: '/p/*/:productId-:colorId',
        component: ProductDetail
    },
    {
        path: '/p/*/:productId',
        component: ProductDetail
    },
    {
        path: '/orderpreparation',
        component: PreparationPage,
        exact: true
    },
    {
        path: '/ps/:productSetId',
        component: ProductSet,
        exact: true
    },
    {
        path: '/giftcard',
        component: GiftCard,
        exact: true
    },
    {
        path: '/sitemap',
        component: Sitemap,
        exact: true
    },
    {
        path: '/*',
        component: LegacyURLs
    },
    {
        path: '*',
        component: PageNotFound
    },
    ..._routes
]

// Outlet routes
const routesForOutlet = [
    {
        path: '/page/:pageId',
        component: PageViewer,
        exact: true
    },
    {
        path: '*',
        component: LegacyURLs
    }
]

export default () => {
    const config = getConfig()
    return configureRoutes(routes, config, routesForOutlet, {
        ignoredRoutes: ['/callback', '*']
    })
}
